import { ReactElement, useState } from 'react';
import { Box } from '@mui/material';
import { 
  User
} from '../../../store';
import { useForm } from 'react-hook-form';

import { CabToggleChip } from '@CabComponents/CabToggleChip';
import colors from '../../../colors';
import { CabIcon } from '@CabComponents/CabIcon';
import { IoAddOutline } from 'react-icons/io5';
import { CabModal } from '@CabComponents/CabModal';
import { CabButton } from '@CabComponents/CabButton';


import { EventLabelAbstractFormInput } from '../../../components/EventAnalytics/Modals/EventLabelAbstractModal';
import {
  CreateEventCategoryModal
} from '../../../components/EventAnalytics/Modals/CreateEventCategoryModal/CreateEventCategoryModal';
import { CompanyCategory, Tag, RelationshipTag } from '../../../store/cabinetApi/generated/crm';
import { DEFAULT_LEADER_LABEL_COLORS } from '../../../constants';


interface Props {
  organizationContactCategories: { [key: number]: CompanyCategory };
  user: User;
  createLabel: (
    data: CompanyCategory, 
  ) => Promise<void>
  updateLabel: (
    data: CompanyCategory, 
  ) => Promise<void>
  saving: boolean;
  deleteLabel: (data: CompanyCategory) => Promise<void>;
}



export const PeopleTags = ({
  organizationContactCategories, user, createLabel, updateLabel, saving, deleteLabel
}: Props): ReactElement => {

  const [selectedTag, setSelectedTag] = useState(-1);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { control, reset, getValues, setValue, formState } = useForm<EventLabelAbstractFormInput>({
    defaultValues: {
      id: -1,
      name:  "",
      color: colors.black200,
      organization: user?.active_license.organization
    }
  });

  const modalTitleStr = selectedTag > 0 ? 'Edit' : 'Create';
  const buttonStr = selectedTag > 0 ? 'Save' : 'Create';
  const modalVariationStr = ' Organization Category';

  const categories = Object.values(organizationContactCategories);


  const createNewCategory = () => {
    reset();
    setSelectedTag(-1);
    setOpenAddEditModal(true);
  };

  const editCategory = (category: CompanyCategory) => {
    setSelectedTag(category.id);
    setValue('id', category.id);
    setValue('name', category.name);
    setValue('color', category.color ?? DEFAULT_LEADER_LABEL_COLORS[0]);
    setOpenAddEditModal(true);
  };

  const submitCleanup = () => {
    setOpenDeleteModal(false);
    setOpenAddEditModal(false);
    setSelectedTag(-1);
    reset();
  };

  const labelSorter = (a:CompanyCategory, b: CompanyCategory) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  };

  const onConfirmClick = async () => {
    if (selectedTag < 0) {
      await createLabel(getValues());
    } else {
      await updateLabel(getValues());
    }
    submitCleanup();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: 1.25
          }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between'
            }}>
            <div></div>
            <CabToggleChip
              label={<Box
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                <CabIcon Icon={IoAddOutline} sx={{fontSize: 20}} />
                New
              </Box>}
              chipColor={colors.black200}
              translateColor
              selected
              onClick={createNewCategory}
              sx={{marginLeft: '16px'}}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1
            }}>
            {categories.sort(labelSorter).map(category => {
              return <CabToggleChip
                key={category.id}
                label={category.name}
                chipColor={category.color}
                translateColor
                selected
                onClick={() => editCategory(category)}
              />;
            })}
          </Box>
        </Box>
      </Box>
      <CabModal
        open={openAddEditModal}
        onClose={() => setOpenAddEditModal(false)}
        closeIcon={true}
        title={modalTitleStr + modalVariationStr}
        actionButtons={
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
            {selectedTag > 0 && (
              <CabButton
                buttonType="tertiary"
                onClick={() => setOpenDeleteModal(true)}
              >
                Delete
              </CabButton>
            )}
            <Box
              sx={{
                gap: 1,
                display: 'flex',
                width: '100%',
                justifyContent: 'flex-end'
              }}>
              <CabButton
                buttonType="secondary"
                onClick={() => setOpenAddEditModal(false)}
              >
                Cancel
              </CabButton>
              <CabButton
                disabled={saving || !formState.isValid}
                onClick={onConfirmClick}
              >
                {buttonStr} Category
              </CabButton>
            </Box>
          </Box>
        }
      >
        <CreateEventCategoryModal
          control={control}
        />
      </CabModal>
      <CabModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        isAlert={true}
        title={'Are you sure?'}
        noFullScreen={true}
        text={`Removing this category will permanently remove it 
        from all events for all users in your organization. Are you sure you want to remove it?`}
        actionButtons={
          <>
            <CabButton buttonType='tertiary' color='primary' onClick={() => setOpenDeleteModal(false)}>
              No
            </CabButton>
            <CabButton 
              onClick={() => deleteLabel(getValues()).then(() => submitCleanup())}
            >
              Yes, delete it
            </CabButton>
          </>
        }
      />
    </>
  );
};

interface ContactTagsProps {
  organizationTags: { [key: number]: Tag };
  user: User;
  createLabel: (
    data: Tag, 
  ) => Promise<void>
  updateLabel: (
    data: Tag, 
  ) => Promise<void>
  saving: boolean;
  deleteLabel: (data: Tag) => Promise<void>;
}

export const ContactTags = ({
  organizationTags, user, createLabel, updateLabel, saving, deleteLabel
}: ContactTagsProps): ReactElement => {

  const [selectedTag, setSelectedTag] = useState(-1);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { control, reset, getValues, setValue, formState } = useForm<EventLabelAbstractFormInput>({
    defaultValues: {
      id: -1,
      name:  "",
      color: colors.black200,
      organization: user?.active_license.organization
    }
  });

  const modalTitleStr = selectedTag > 0 ? 'Edit' : 'Create';
  const buttonStr = selectedTag > 0 ? 'Save' : 'Create';
  const modalVariationStr = ' Tag';

  const tags = Object.values(organizationTags);


  const createNewTag = () => {
    reset();
    setSelectedTag(-1);
    setOpenAddEditModal(true);
  };

  const editTag = (tag: Tag) => {
    setSelectedTag(tag.id);
    setValue('id', tag.id);
    setValue('name', tag.name);
    setValue('color', tag.color ?? DEFAULT_LEADER_LABEL_COLORS[0]);
    setOpenAddEditModal(true);
  };

  const submitCleanup = () => {
    setOpenDeleteModal(false);
    setOpenAddEditModal(false);
    setSelectedTag(-1);
    reset();
  };

  const tagSorter = (a: Tag, b: Tag) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  };

  const onConfirmClick = async () => {
    if (selectedTag < 0) {
      await createLabel(getValues());
    } else {
      await updateLabel(getValues());
    }
    submitCleanup();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: 1.25
          }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between'
            }}>
            <div></div>
            <CabToggleChip
              label={<Box
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                <CabIcon Icon={IoAddOutline} sx={{fontSize: 20}} />
                New
              </Box>}
              chipColor={colors.black200}
              translateColor
              selected
              onClick={createNewTag}
              sx={{marginLeft: '16px'}}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1
            }}>
            {tags.sort(tagSorter).map(tag => {
              return <CabToggleChip
                key={tag.id}
                label={tag.name}
                chipColor={tag.color}
                translateColor
                selected
                onClick={() => editTag(tag)}
              />;
            })}
          </Box>
        </Box>
      </Box>
      <CabModal
        open={openAddEditModal}
        onClose={() => setOpenAddEditModal(false)}
        closeIcon={true}
        title={modalTitleStr + modalVariationStr}
        actionButtons={
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
            {selectedTag > 0 && (
              <CabButton
                buttonType="tertiary"
                onClick={() => setOpenDeleteModal(true)}
              >
                Delete
              </CabButton>
            )}
            <Box
              sx={{
                gap: 1,
                display: 'flex',
                width: '100%',
                justifyContent: 'flex-end'
              }}>
              <CabButton
                buttonType="secondary"
                onClick={() => setOpenAddEditModal(false)}
              >
                Cancel
              </CabButton>
              <CabButton
                disabled={saving || !formState.isValid}
                onClick={onConfirmClick}
              >
                {buttonStr} Tag
              </CabButton>
            </Box>
          </Box>
        }
      >
        <CreateEventCategoryModal
          control={control}
        />
      </CabModal>
      <CabModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        isAlert={true}
        title={'Are you sure?'}
        noFullScreen={true}
        text={`Removing this tag will permanently remove it 
        from all contacts for all users in your organization. Are you sure you want to remove it?`}
        actionButtons={
          <>
            <CabButton buttonType='tertiary' color='primary' onClick={() => setOpenDeleteModal(false)}>
              No
            </CabButton>
            <CabButton 
              onClick={() => deleteLabel(getValues()).then(() => submitCleanup())}
            >
              Yes, delete it
            </CabButton>
          </>
        }
      />
    </>
  );
};

interface RelationshipTagsProps {
  organizationTags: { [key: number]: RelationshipTag };
  user: User;
  createLabel: (
    data: RelationshipTag, 
  ) => Promise<void>
  updateLabel: (
    data: RelationshipTag, 
  ) => Promise<void>
  saving: boolean;
  deleteLabel: (data: RelationshipTag) => Promise<void>;
}

export const RelationshipTags = ({
  organizationTags, user, createLabel, updateLabel, saving, deleteLabel
}: RelationshipTagsProps): ReactElement => {

  const [selectedTag, setSelectedTag] = useState(-1);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { control, reset, getValues, setValue, formState } = useForm<EventLabelAbstractFormInput>({
    defaultValues: {
      id: -1,
      name:  "",
      color: colors.black200,
      organization: user?.active_license.organization
    }
  });

  const modalTitleStr = selectedTag > 0 ? 'Edit' : 'Create';
  const buttonStr = selectedTag > 0 ? 'Save' : 'Create';
  const modalVariationStr = ' Tag';

  const tags = Object.values(organizationTags);


  const createNewTag = () => {
    reset();
    setSelectedTag(-1);
    setOpenAddEditModal(true);
  };

  const editTag = (tag: RelationshipTag) => {
    setSelectedTag(tag.id);
    setValue('id', tag.id);
    setValue('name', tag.name);
    setValue('color', tag.color ?? DEFAULT_LEADER_LABEL_COLORS[0]);
    setOpenAddEditModal(true);
  };

  const submitCleanup = () => {
    setOpenDeleteModal(false);
    setOpenAddEditModal(false);
    setSelectedTag(-1);
    reset();
  };

  const tagSorter = (a: RelationshipTag, b: RelationshipTag) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  };

  const onConfirmClick = async () => {
    if (selectedTag < 0) {
      await createLabel(getValues());
    } else {
      await updateLabel(getValues());
    }
    submitCleanup();
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: 1.25
          }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between'
            }}>
            <div></div>
            <CabToggleChip
              label={<Box
                sx={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                <CabIcon Icon={IoAddOutline} sx={{fontSize: 20}} />
                New
              </Box>}
              chipColor={colors.black200}
              translateColor
              selected
              onClick={createNewTag}
              sx={{marginLeft: '16px'}}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1
            }}>
            {tags.sort(tagSorter).map(tag => {
              return <CabToggleChip
                key={tag.id}
                label={tag.name}
                chipColor={tag.color}
                translateColor
                selected
                onClick={() => editTag(tag)}
              />;
            })}
          </Box>
        </Box>
      </Box>
      <CabModal
        open={openAddEditModal}
        onClose={() => setOpenAddEditModal(false)}
        closeIcon={true}
        title={modalTitleStr + modalVariationStr}
        actionButtons={
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
            {selectedTag > 0 && (
              <CabButton
                buttonType="tertiary"
                onClick={() => setOpenDeleteModal(true)}
              >
                Delete
              </CabButton>
            )}
            <Box
              sx={{
                gap: 1,
                display: 'flex',
                width: '100%',
                justifyContent: 'flex-end'
              }}>
              <CabButton
                buttonType="secondary"
                onClick={() => setOpenAddEditModal(false)}
              >
                Cancel
              </CabButton>
              <CabButton
                disabled={saving || !formState.isValid}
                onClick={onConfirmClick}
              >
                {buttonStr} Tag
              </CabButton>
            </Box>
          </Box>
        }
      >
        <CreateEventCategoryModal
          control={control}
        />
      </CabModal>
      <CabModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        isAlert={true}
        title={'Are you sure?'}
        noFullScreen={true}
        text={`Removing this tag will permanently remove it 
        from all relationships for all users in your organization. Are you sure you want to remove it?`}
        actionButtons={
          <>
            <CabButton buttonType='tertiary' color='primary' onClick={() => setOpenDeleteModal(false)}>
              No
            </CabButton>
            <CabButton 
              onClick={() => deleteLabel(getValues()).then(() => submitCleanup())}
            >
              Yes, delete it
            </CabButton>
          </>
        }
      />
    </>
  );
};
  
export default PeopleTags;